import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * The state capsule — the design system's `.badge`, bound to its tokens.
 *
 * **The taxonomy is closed.** Seven kinds ship and an eighth with a new colour
 * is forbidden; a new state borrows the nearest existing meaning. The mapping
 * from a vocabulary's values onto these kinds is assigned in the design system
 * once, so no surface picks its own — and a value the design system has not
 * mapped renders `neutral` with its literal label rather than an invented
 * colour.
 *
 * **Colour is never the sole carrier of the state.** The label is the state,
 * written out, which is what makes the badge readable to someone who cannot
 * tell the two soft fills apart. An icon is accepted where a surface's design
 * draws one; the label is not optional and never becomes an attribute.
 *
 * **The badge is the single source of a row's state.** Where a row carries a
 * menu, that menu derives what it offers by reading this cell — so the cell is
 * selected by its column, never by whichever badge appears first in the row.
 */
export type BadgeKind = 'neutral' | 'accent' | 'editorial' | 'success' | 'warning' | 'danger';

const KIND_CLASSES: Record<BadgeKind, string> = {
  neutral: 'border-border bg-surface-overlay text-ink-muted',
  accent: 'border-accent bg-accent-soft text-accent-text',
  editorial: 'border-editorial bg-editorial-soft text-editorial',
  success: 'border-success bg-success-soft text-success',
  warning: 'border-warning bg-warning-soft text-warning',
  danger: 'border-danger bg-danger-soft text-danger',
};

export function StateBadge({
  kind = 'neutral',
  icon,
  children,
  className,
}: {
  kind?: BadgeKind;
  /** Drawn only where the surface's design carries one; never a substitute for the label. */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-3 py-1 text-xs font-medium',
        KIND_CLASSES[kind],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}
